/**
 * FinanceiroContasReceberPage — aba "Contas a Receber" (Fase 2, Zeus-only).
 *
 * Visão pros sócios do recebível de ENERGIA a vencer (vendas vigentes
 * externas), consumindo GET /api/contas-receber (PR #312/#313). Fase 2:
 * top_clientes vem CONSOLIDADO por GRUPO ECONÔMICO (id_grupo/grupo_nome do
 * Zeus), com breakdown das entidades sob cada grupo. total/cronograma não
 * mudam (mesma base) — só a dimensão de cliente.
 *
 * Layout (cima→baixo): KPIs · Top grupos (ranking + barra + breakdown
 * expansível) · Cronograma (4 buckets) · rodapé com caveats honestos.
 *
 * Semântica de cor: ENTRADA = turquesa/verde (indra-positivo). Não confia só
 * na cor — KPI traz ícone + rótulo "energia a vencer" (acessibilidade).
 */
import { useState } from "react";
import {
  TrendingUp,
  Users,
  PieChart,
  CalendarClock,
  ChevronRight,
  ChevronDown,
  Layers,
} from "lucide-react";
import { KpiCard } from "../components/ui/KpiCard";
import { Loading } from "../components/Loading";
import { ContasReceberCronograma } from "../components/financeiro/ContasReceberCronograma";
import { useContasReceber } from "../hooks/useContasReceber";
import type { TopClienteReceber } from "../api/types";
import { fmtBRL, fmtBRLcompact, fmtPct } from "../utils/formatters";
import { formatarDataCurtaBr } from "../utils/dateBr";

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function ClienteRow({
  c,
  posicao,
  expanded,
  onToggle,
}: {
  c: TopClienteReceber;
  posicao: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const top5 = posicao <= 5;
  // Só vale expandir quando o grupo tem mais de uma entidade.
  const expansivel = c.is_grupo && c.n_entidades > 1;
  return (
    <>
      <tr
        className={
          "border-b border-white/5 hover:bg-white/[0.02] " +
          (expansivel ? "cursor-pointer" : "")
        }
        onClick={expansivel ? onToggle : undefined}
        data-testid={`cr-cliente-row-${posicao - 1}`}
      >
        <td className="px-4 py-2.5">
          <span
            className={
              "inline-flex items-center justify-center h-6 w-6 rounded text-[11px] font-bold " +
              (top5 ? "bg-indra-positivo/20 text-indra-positivo" : "bg-white/10 text-white/50")
            }
          >
            {posicao}
          </span>
        </td>
        <td className="px-4 py-2.5 text-white/90">
          <div className="flex items-center gap-2">
            {expansivel ? (
              expanded ? (
                <ChevronDown size={14} className="text-white/50 shrink-0" />
              ) : (
                <ChevronRight size={14} className="text-white/50 shrink-0" />
              )
            ) : (
              <span className="w-[14px] shrink-0" />
            )}
            <span className={top5 ? "font-semibold" : ""}>{c.cliente}</span>
            {c.is_grupo && (
              <span
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] bg-indra-turquesa/15 text-indra-turquesa border border-indra-turquesa/30"
                title={`Grupo econômico do Zeus (${c.n_entidades} ${c.n_entidades === 1 ? "entidade" : "entidades"})`}
                data-testid={`cr-badge-grupo-${posicao - 1}`}
              >
                <Layers size={9} /> grupo · {c.n_entidades}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-2.5 text-right font-mono text-white/90 whitespace-nowrap">
          {fmtBRL(c.valor, { decimals: 0 })}
        </td>
        <td className="px-4 py-2.5 text-right font-mono text-white/70">{fmtPct(c.pct_do_total)}</td>
        <td className="px-4 py-2.5">
          <div className="h-2 rounded bg-white/5">
            <div
              className={"h-2 rounded " + (top5 ? "bg-indra-positivo/80" : "bg-indra-positivo/40")}
              style={{ width: `${Math.min(100, Math.max(2, c.pct_do_total))}%` }}
            />
          </div>
        </td>
      </tr>
      {expansivel && expanded && (
        <tr className="bg-indra-azul-deep/40" data-testid={`cr-breakdown-${posicao - 1}`}>
          <td />
          <td className="px-4 py-2" colSpan={4}>
            <div className="text-[11px] text-white/50 mb-1">Entidades sob {c.cliente}</div>
            <ul className="space-y-1">
              {c.entidades.map((e) => (
                <li key={e.nome} className="flex items-center justify-between gap-4 text-xs">
                  <span className="text-white/70">{e.nome}</span>
                  <span className="font-mono text-white/60 whitespace-nowrap">
                    {fmtBRL(e.valor, { decimals: 0 })}
                  </span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

export function FinanceiroContasReceberPage() {
  const { data, isLoading, error } = useContasReceber();
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());

  const toggle = (i: number) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div data-testid="financeiro-contas-receber-page">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-white font-sans">Contas a Receber</h1>
        <p className="text-white/50 text-sm mt-1">
          Recebível de energia a vencer, consolidado por grupo econômico. Origem: Zeus (real).
        </p>
      </div>

      {error ? (
        <div
          className="rounded-xl border border-indra-negativo/30 bg-indra-negativo/10 p-6 text-sm text-white/80"
          data-testid="contas-receber-error"
        >
          Não foi possível carregar as contas a receber. Verifique a conexão e tente novamente.
        </div>
      ) : !data || isLoading ? (
        <div className="flex items-center justify-center py-20" data-testid="contas-receber-loading">
          <Loading label="Carregando contas a receber…" />
        </div>
      ) : data.num_trades === 0 ? (
        <div className="text-center py-16 text-white/40" data-testid="contas-receber-empty">
          Nenhum recebível de energia a vencer no momento.
        </div>
      ) : (
        <>
          {/* 1. KPIs ----------------------------------------------------------- */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6"
            data-testid="contas-receber-kpis"
          >
            <KpiCard
              label="Total a receber"
              value={fmtBRL(data.total_a_receber, { abbreviate: true })}
              sub="Energia a vencer (entrada)"
              subTone="positive"
              icon={<TrendingUp size={16} className="text-indra-positivo" />}
              highlight
              testId="kpi-cr-total"
            />
            <KpiCard
              label="Grupos / clientes"
              value={data.num_grupos.toLocaleString("pt-BR")}
              sub={`${data.num_clientes.toLocaleString("pt-BR")} contrapartes · ${data.num_trades.toLocaleString("pt-BR")} trades`}
              subTone="muted"
              icon={<Users size={16} />}
              testId="kpi-cr-clientes"
            />
            <KpiCard
              label="Concentração top-5"
              value={fmtPct(data.concentracao_top5_pct)}
              sub="dos 5 maiores grupos"
              subTone="muted"
              icon={<PieChart size={16} />}
              testId="kpi-cr-concentracao"
            />
            <KpiCard
              label="Próximo 6DU"
              value={
                data.cronograma.proximo_6du_data
                  ? fmtBRLcompact(data.cronograma.proximo_6du_valor)
                  : "—"
              }
              sub={
                data.cronograma.proximo_6du_data
                  ? formatarDataCurtaBr(parseIso(data.cronograma.proximo_6du_data))
                  : "calendário CCEE não cobre"
              }
              subTone={data.cronograma.proximo_6du_data ? "positive" : "muted"}
              icon={<CalendarClock size={16} />}
              testId="kpi-cr-proximo-6du"
            />
          </div>

          {/* 2. Top grupos ---------------------------------------------------- */}
          <div className="mb-6">
            <div className="mb-2 flex items-end justify-between gap-3 flex-wrap">
              <h2 className="text-sm font-bold text-white">Top grupos econômicos</h2>
              <span className="text-xs text-white/50" data-testid="cr-concentracao-inline">
                Top-5 concentra{" "}
                <span className="text-indra-positivo font-semibold">
                  {fmtPct(data.concentracao_top5_pct)}
                </span>{" "}
                do total
              </span>
            </div>

            <div
              className="bg-indra-azul-surface rounded-xl border border-white/10 overflow-hidden"
              data-testid="cr-top-clientes-table"
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-white/50 border-b border-white/10">
                    <th className="px-4 py-3 w-10">#</th>
                    <th className="px-4 py-3">Grupo / cliente</th>
                    <th className="px-4 py-3 text-right">Valor</th>
                    <th className="px-4 py-3 text-right w-20">% total</th>
                    <th className="px-4 py-3 w-40">Participação</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_clientes.map((c, i) => (
                    <ClienteRow
                      key={`${c.cliente}-${i}`}
                      c={c}
                      posicao={i + 1}
                      expanded={expandidos.has(i)}
                      onToggle={() => toggle(i)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1.5 text-[11px] text-white/40">
              Grupos com mais de uma entidade são expansíveis (clique para ver a composição).
            </p>
          </div>

          {/* 3. Cronograma --------------------------------------------------- */}
          <div className="mb-6">
            <ContasReceberCronograma cronograma={data.cronograma} asof={data.asof} />
          </div>

          {/* 4. Rodapé / caveats -------------------------------------------- */}
          <div
            className="rounded-xl border border-white/10 bg-indra-azul-deep/40 p-4 text-xs text-white/50 space-y-1.5"
            data-testid="contas-receber-caveats"
          >
            <p>
              <span className="text-white/70 font-semibold">Fase 1:</span> energia a vencer (Zeus).
              Recebido × atrasado = Fase 2.
            </p>
            <p>
              Consolidação por grupo econômico do Zeus; varejo exibido por cliente; relação
              SAFIRA↔GENIAL não é modelada no Zeus → pendente validação Tati (Fase 2b).
            </p>
            <p className="text-white/40">
              {data.fonte_label} · referência {formatarDataCurtaBr(parseIso(data.asof))}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
